import React, { useState, useEffect } from "react";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import clockTokens from "@amzn/meridian-tokens/base/icon/clock";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Expander, Icon } from "@amzn/meridian";
import Loader from "@amzn/meridian/loader";
import {
  WasteProfilePathway,
  ClassifyWasteProfileResponse,
  SdsAttribute,
} from "src/types/classificationTypes";
import { classifyWasteProfile } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import { useNavigate, useLocation } from "react-router-dom";
import WasteProfileFeedback from "./WasteProfileFeedback";
import "./classification.scss";

function RemainingProfilesExpander({ remainingProfileIds }: { remainingProfileIds: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (remainingProfileIds.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander title="View more profiles" type="inline" open={expanded} onChange={setExpanded}>
        <Column spacing="300" spacingInset="none">
          {remainingProfileIds.map((id, idx) => (
            <React.Fragment key={idx}>
              <Text type="h100">{id}</Text>
              {idx < remainingProfileIds.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

function ReasoningExpander({ reasoning }: { reasoning: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <Divider />
      <Expander
        title="Why these profiles?"
        type="inline"
        open={expanded}
        onChange={setExpanded}
        spacingInset="none"
      >
        <Text type="b200" color="secondary">
          {reasoning}
        </Text>
      </Expander>
    </>
  );
}

const SDS_LIST_COLLAPSE_THRESHOLD = 6;

function SdsAttributeValue({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);
  const items = value.split(", ");
  if (items.length <= SDS_LIST_COLLAPSE_THRESHOLD) {
    return <Text type="b200">{value}</Text>;
  }
  const hiddenCount = items.length - SDS_LIST_COLLAPSE_THRESHOLD;
  const display = expanded
    ? value
    : `${items.slice(0, SDS_LIST_COLLAPSE_THRESHOLD).join(", ")}, +${hiddenCount} more`;
  return (
    <Column spacing="100" spacingInset="none" alignmentHorizontal="start">
      <Text type="b200">{display}</Text>
      <Button type="link" size="small" onClick={() => setExpanded((e) => !e)}>
        {expanded ? "Show less" : "Show all"}
      </Button>
    </Column>
  );
}

function SdsAttributesExpander({ sdsAttributes }: { sdsAttributes: SdsAttribute[] }) {
  const [expanded, setExpanded] = useState(false);
  if (sdsAttributes.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander
        title="View SDS attributes"
        type="inline"
        spacingInset="none"
        open={expanded}
        onChange={setExpanded}
      >
        <Column width="100%" spacing="200" spacingInset="none">
          {sdsAttributes.map((attribute, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Row width="100%" widths={["35%", "fill"]} alignmentVertical="top" spacing="300">
                <Text type="b200" color="secondary">
                  {attribute.name}
                </Text>
                <SdsAttributeValue value={attribute.value} />
              </Row>
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

interface WasteProfileCardProps {
  asin: string;
  itemName?: string;
  onLoadingChange?: (value: boolean) => void;
  onPathwayChange?: (pathway: WasteProfilePathway) => void;
  onClassificationIdChange?: (classificationId: string) => void;
  isFeedbackCaptured?: boolean;
  savedWasteProfileData?: ClassifyWasteProfileResponse;
}

interface WasteProfileLayoutProps {
  headerClassName?: string;
  headerIcon?: object;
  headerLabel?: string;
  invertHeader?: boolean;
  recommendedWasteProfilesIds: string[];
  remainingProfiles: string[];
  reasoning?: string;
  sdsAttributes?: SdsAttribute[];
  feedbackContent?: React.ReactNode;
}

const WasteProfileLayout: React.FC<WasteProfileLayoutProps> = ({
  headerClassName = "",
  headerIcon,
  headerLabel,
  invertHeader = true,
  recommendedWasteProfilesIds,
  remainingProfiles,
  reasoning,
  sdsAttributes,
  feedbackContent,
}) => (
  <Column className={`home-card ${headerClassName}`} spacingInset="400 none none">
    {headerIcon && headerLabel && (
      <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
        {invertHeader ? (
          <Icon color="inverted" tokens={headerIcon} />
        ) : (
          <Icon tokens={headerIcon} />
        )}
        <Text type="h200" color={invertHeader ? "inverted" : "primary"}>
          {headerLabel}
        </Text>
      </Row>
    )}
    <Column className="home-card" overflowY="hidden" spacingInset="none">
      <Column spacingInset="400 500 none" spacing="300">
        {recommendedWasteProfilesIds.map((profileId, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <Divider />}
            <Text type="h200">{profileId}</Text>
          </React.Fragment>
        ))}
        <RemainingProfilesExpander remainingProfileIds={remainingProfiles} />
        {sdsAttributes && <SdsAttributesExpander sdsAttributes={sdsAttributes} />}
        {reasoning && <ReasoningExpander reasoning={reasoning} />}
      </Column>
      {feedbackContent && (
        <>
          <Divider />
          <Column spacingInset="none">{feedbackContent}</Column>
        </>
      )}
    </Column>
  </Column>
);

function WasteProfileCard({
  asin,
  itemName,
  onLoadingChange,
  onPathwayChange,
  onClassificationIdChange,
  isFeedbackCaptured,
  savedWasteProfileData,
}: WasteProfileCardProps) {
  const { activeSiteCode } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [wasteProfileData, setWasteProfileData] = useState<ClassifyWasteProfileResponse | null>(
    savedWasteProfileData ?? null,
  );
  const [isLoading, setIsLoading] = useState(!savedWasteProfileData);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (savedWasteProfileData) {
      onPathwayChange?.(savedWasteProfileData.pathway);
      onClassificationIdChange?.(savedWasteProfileData.classificationId);
      onLoadingChange?.(false);
      return;
    }

    let cancelled = false;

    const fetchClassification = async () => {
      setIsLoading(true);
      setWasteProfileData(null);
      try {
        const res = await classifyWasteProfile({
          itemName: itemName ?? "",
          asin,
          siteName: activeSiteCode,
        });
        if (!cancelled) {
          setWasteProfileData(res);
          onPathwayChange?.(res.pathway);
          onClassificationIdChange?.(res.classificationId);
          // Persist the result to the current history entry so navigating back to this page skips the API call and uses the saved data directly.
          navigate(".", {
            replace: true,
            state: { ...location.state, savedWasteProfileData: res },
          });
        }
      } catch (error) {
        console.error("[WasteProfileCard] Failed to classify waste profile:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    onLoadingChange?.(true);
    fetchClassification();
    return () => {
      cancelled = true;
    };
  }, [asin, itemName, activeSiteCode, retryCount]);

  if (isLoading) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Row spacing="300" alignmentVertical="center">
          <Loader size="small" type="circular" />
          <Text type="b300" color="secondary">
            Loading waste profile...
          </Text>
        </Row>
      </Column>
    );
  }

  if (!wasteProfileData) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Text type="b300" color="error">
          Unable to load waste profile. Please try again.
        </Text>
        <Button onClick={() => setRetryCount((c) => c + 1)} size="small" type="secondary">
          Retry
        </Button>
      </Column>
    );
  }

  const { recommendedWasteProfilesIds, allWasteProfilesIds, pathway, reasoning, sdsAttributes } =
    wasteProfileData;

  const recommendedSet = new Set(recommendedWasteProfilesIds);
  const remainingProfiles = allWasteProfilesIds.filter((id) => !recommendedSet.has(id));

  const feedbackContent = (
    <WasteProfileFeedback
      asin={asin}
      classificationId={wasteProfileData.classificationId}
      wasteProfileIds={pathway === WasteProfilePathway.DEFAULT ? [] : recommendedWasteProfilesIds}
      siteName={activeSiteCode}
      pathway={pathway}
      isFeedbackCaptured={isFeedbackCaptured}
    />
  );

  if (pathway === WasteProfilePathway.GOLDEN_ASIN) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile--golden"
        headerIcon={starFilledTokens}
        headerLabel="Golden ASIN Profile"
        invertHeader={false}
        recommendedWasteProfilesIds={allWasteProfilesIds}
        remainingProfiles={[]}
        reasoning={reasoning}
        feedbackContent={feedbackContent}
      />
    );
  }

  if (pathway === WasteProfilePathway.SDS_EXTRACTION) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile--sds"
        headerIcon={documentTokens}
        headerLabel="SDS Attribute Based Recommendation"
        recommendedWasteProfilesIds={recommendedWasteProfilesIds}
        remainingProfiles={remainingProfiles}
        reasoning={reasoning}
        sdsAttributes={sdsAttributes}
        feedbackContent={feedbackContent}
      />
    );
  }

  if (pathway === WasteProfilePathway.HISTORICAL) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile--historical"
        headerIcon={clockTokens}
        headerLabel="Historical Classification"
        recommendedWasteProfilesIds={recommendedWasteProfilesIds}
        remainingProfiles={remainingProfiles}
        reasoning={reasoning}
        feedbackContent={feedbackContent}
      />
    );
  }

  if (
    pathway === WasteProfilePathway.SIMILARITY ||
    pathway === WasteProfilePathway.AI_EXTERNAL_RESEARCH
  ) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile"
        headerIcon={sparklesSmallTokens}
        headerLabel="Profile Recommended by AI"
        recommendedWasteProfilesIds={recommendedWasteProfilesIds}
        remainingProfiles={remainingProfiles}
        reasoning={reasoning}
        feedbackContent={feedbackContent}
      />
    );
  }

  // DEFAULT — no header
  return (
    <WasteProfileLayout
      recommendedWasteProfilesIds={recommendedWasteProfilesIds}
      remainingProfiles={remainingProfiles}
      reasoning={reasoning}
      feedbackContent={feedbackContent}
    />
  );
}

export default WasteProfileCard;
